// src/pages/GoogleAdsCampaigns.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiCalendar,
  FiBarChart2,
  FiPlay,
  FiPause,
  FiSmartphone,
  FiVideo,
  FiSearch,
  FiRefreshCw,
  FiAlertTriangle,
} from "react-icons/fi";

import GoogleAdsConnection from "../components/GoogleAdsConnection";
import { adsApi, formatMoney } from "../lib/googleAds";

/* -------------------------
   Meta / helpers
-------------------------- */
const STATUS_META = {
  ENABLED: { label: "enabled", bg: "bg-green-100", text: "text-green-800", ring: "ring-green-200", icon: <FiPlay className="w-3.5 h-3.5" /> },
  PAUSED: { label: "paused", bg: "bg-yellow-100", text: "text-yellow-800", ring: "ring-yellow-200", icon: <FiPause className="w-3.5 h-3.5" /> },
  REMOVED: { label: "removed", bg: "bg-red-100", text: "text-red-800", ring: "ring-red-200", icon: null },
  UNSPECIFIED: { label: "unspecified", bg: "bg-gray-100", text: "text-gray-700", ring: "ring-gray-200", icon: null },
  UNKNOWN: { label: "unknown", bg: "bg-gray-100", text: "text-gray-700", ring: "ring-gray-200", icon: null },
};

const CHANNEL_META = {
  SMART: { label: "Smart", bg: "bg-brand-100", text: "text-brand-800", icon: <FiSmartphone className="w-3.5 h-3.5" /> },
  VIDEO: { label: "Video", bg: "bg-brand-100", text: "text-brand-800", icon: <FiVideo className="w-3.5 h-3.5" /> },
  SEARCH: { label: "Search", bg: "bg-brand-100", text: "text-brand-800", icon: <FiSearch className="w-3.5 h-3.5" /> },
  DISPLAY: { label: "Display", bg: "bg-orange-100", text: "text-orange-800", icon: <FiBarChart2 className="w-3.5 h-3.5" /> },
};

function Badge({ meta, children, className = "" }) {
  if (!meta) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.text} ring-1 ${meta.ring} ${className}`}>
      {meta.icon}
      {children}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="h-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm animate-pulse">
      <div className="flex justify-between items-start mb-4">
        <div className="h-5 w-2/3 bg-gray-200 rounded" />
        <div className="h-6 w-20 bg-gray-200 rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="h-4 w-28 bg-gray-200 rounded" />
        <div className="h-4 w-24 bg-gray-200 rounded" />
      </div>
      <div className="h-4 w-full bg-gray-200 rounded mb-2" />
      <div className="h-4 w-2/3 bg-gray-200 rounded" />
      <div className="pt-4 mt-4 border-t border-gray-100 flex justify-between">
        <div className="h-4 w-40 bg-gray-200 rounded" />
        <div className="h-4 w-24 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

/* -------------------------
   Cards
-------------------------- */
function CampaignCard({ campaign, onNameClick }) {
  const statusMeta = STATUS_META[campaign.status] || STATUS_META.UNKNOWN;
  const channelMeta = CHANNEL_META[campaign.channel] || null;

  return (
    <div className="group h-full w-full text-left bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md hover:border-gray-300 transition-all">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3
            onClick={(e) => {
              e.stopPropagation();
              onNameClick?.(campaign);
            }}
            className="cursor-pointer text-base sm:text-lg font-semibold text-gray-900 truncate group-hover:underline"
            title="Open summary"
          >
            {campaign.name}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge meta={statusMeta}>{(statusMeta?.label || campaign.status).toLowerCase()}</Badge>
            {channelMeta && <Badge meta={channelMeta}>{channelMeta.label}</Badge>}
          </div>
        </div>
        <div className="text-[11px] text-gray-400 font-mono">
          {campaign.resource_name?.split("/").pop()}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-500">Campaign ID</div>
          <div className="font-medium text-gray-900">{campaign.id}</div>
        </div>
        <div>
          <div className="text-gray-500">Channel</div>
          <div className="font-medium text-gray-900">{campaign.channel || "—"}</div>
        </div>
      </div>

      <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FiCalendar className="w-4 h-4 text-gray-500" />
          <span className="font-medium">{campaign.start_date || "—"}</span>
          <span className="mx-1 text-gray-400">–</span>
          <span className="font-medium">{campaign.end_date || "—"}</span>
        </div>
      </div>
    </div>
  );
}

function BudgetCard({ budget, currency }) {
  const statusMeta = STATUS_META[budget.status] || STATUS_META.UNKNOWN;
  // The backend sends both: `amount` in currency units for reading, and
  // `amount_micros` for anything that has to round-trip the exact value.
  const daily = budget.amount != null
    ? budget.amount
    : Number(budget.amount_micros || 0) / 1_000_000;

  return (
    <div className="h-full bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md hover:border-gray-300 transition-all">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
            {budget.name}
          </h3>
          <div className="mt-1">
            <Badge meta={statusMeta}>{(statusMeta?.label || budget.status).toLowerCase()}</Badge>
          </div>
        </div>
        <div className="text-[11px] text-gray-400 font-mono">
          {budget.resource_name?.split("/").pop()}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-500">ID</div>
          <div className="font-medium text-gray-900">{budget.id}</div>
        </div>
        <div>
          <div className="text-gray-500">Daily amount</div>
          <div className="font-medium text-gray-900">{formatMoney(daily, currency)}</div>
        </div>
        <div>
          <div className="text-gray-500">Delivery</div>
          <div className="font-medium text-gray-900">{budget.delivery_method || "—"}</div>
        </div>
        <div>
          <div className="text-gray-500">Shared</div>
          <div className="font-medium text-gray-900">{budget.shared ? "Yes" : "No"}</div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------
   Page
-------------------------- */
export default function GoogleAdsCampaigns() {
  const navigate = useNavigate();

  // State
  const [connected, setConnected] = useState(null); // null while status loads
  const [campaigns, setCampaigns] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [currency, setCurrency] = useState("INR");
  const [account, setAccount] = useState(null);

  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingBudgets, setLoadingBudgets] = useState(true);

  const [errCampaigns, setErrCampaigns] = useState("");
  const [errBudgets, setErrBudgets] = useState("");

  // Filters / search
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [channelFilter, setChannelFilter] = useState("ALL");
  const [budgetQuery, setBudgetQuery] = useState("");

  /* ------- Fetchers ------- */
  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    setErrCampaigns("");
    try {
      const data = await adsApi("/google/list_campaigns");
      setCampaigns(Array.isArray(data?.campaigns) ? data.campaigns : []);
    } catch (e) {
      setErrCampaigns(e.message);
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  const fetchBudgets = useCallback(async () => {
    setLoadingBudgets(true);
    setErrBudgets("");
    try {
      const data = await adsApi("/google/list_budgets");
      setBudgets(Array.isArray(data?.budgets) ? data.budgets : []);
    } catch (e) {
      setErrBudgets(e.message);
      setBudgets([]);
    } finally {
      setLoadingBudgets(false);
    }
  }, []);

  // The account this stack operates on, and what it bills in. Comes from the
  // MCC listing rather than a constant in the bundle — the page used to print
  // "Server default" and the summary page hard-coded one customer id.
  const fetchAccount = useCallback(async () => {
    try {
      const data = await adsApi("/google/list_children");
      const active = String(data?.active_customer_id || "");
      const children = Array.isArray(data?.children) ? data.children : [];
      const mine = children.find((c) => String(c.id) === active) || null;
      setAccount(mine || (active ? { id: active } : null));
      if (mine?.currency_code) setCurrency(mine.currency_code);
    } catch {
      // Not fatal: the lists below still render, just labelled in the default
      // currency. The connection panel already reports a real outage.
    }
  }, []);

  // Only once we know an account is connected — otherwise all three calls fail
  // with "No refresh token stored" and bury the panel that explains why.
  useEffect(() => {
    if (!connected) {
      if (connected === false) {
        setLoadingCampaigns(false);
        setLoadingBudgets(false);
      }
      return;
    }
    fetchCampaigns();
    fetchBudgets();
    fetchAccount();
  }, [connected, fetchCampaigns, fetchBudgets, fetchAccount]);

  /* ------- Derived ------- */
  const filteredCampaigns = useMemo(() => {
    const q = query.trim().toLowerCase();
    return campaigns.filter((c) => {
      const matchesQuery =
        !q ||
        c.name?.toLowerCase().includes(q) ||
        String(c.id || "").includes(q) ||
        c.channel?.toLowerCase().includes(q);
      const matchesStatus = statusFilter === "ALL" || c.status === statusFilter;
      const matchesChannel = channelFilter === "ALL" || c.channel === channelFilter;
      return matchesQuery && matchesStatus && matchesChannel;
    });
  }, [campaigns, query, statusFilter, channelFilter]);

  const filteredBudgets = useMemo(() => {
    const q = budgetQuery.trim().toLowerCase();
    return budgets.filter((b) => {
      if (!q) return true;
      return (
        b.name?.toLowerCase().includes(q) ||
        String(b.id || "").includes(q) ||
        String(b.amount_micros || "").includes(q) ||
        String(b.status || "").toLowerCase().includes(q) ||
        String(b.shared || "").toLowerCase().includes(q)
      );
    });
  }, [budgets, budgetQuery]);

  /* ------- Handlers ------- */
  const handleNameClick = (campaign) => {
    navigate("/google-ads/summary", {
      state: { campaign }, // only pass what we need now
    });
  };

  /* -------------------------
     Render
  -------------------------- */
  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mx-auto" style={{ maxWidth: "1800px" }}>
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Google Ads</h1>
            <p className="text-sm sm:text-base text-gray-600">Manage and monitor your Google Ads campaigns & budgets</p>
          </div>

          {/* Connection */}
          <GoogleAdsConnection onChange={setConnected} next="/google-ads" />

          {/* Controls */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1">
                <div className="text-sm text-gray-500">Account</div>
                <div className="text-lg font-semibold text-gray-900">
                  {account?.descriptive_name || account?.id || "—"}
                </div>
                {account?.id && (
                  <div className="text-xs text-gray-500">
                    {account.id}
                    {account.test_account && (
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">
                        test account
                      </span>
                    )}
                    <span className="ml-2">{currency}</span>
                  </div>
                )}
              </div>

              {/* Campaign search + filters */}
              <div className="flex-1 w-full">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search campaigns by name, ID, or channel…"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>

              <div className="flex gap-2 w-full lg:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="flex-1 lg:flex-none px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="ALL">All Statuses</option>
                  {Object.keys(STATUS_META).map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>

                <select
                  value={channelFilter}
                  onChange={(e) => setChannelFilter(e.target.value)}
                  className="flex-1 lg:flex-none px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="ALL">All Channels</option>
                  {Object.keys(CHANNEL_META).map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={fetchCampaigns}
                  disabled={!connected}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-ink rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50"
                >
                  <FiRefreshCw className="w-4 h-4" />
                  Refresh Campaigns
                </button>
                <button
                  onClick={fetchBudgets}
                  disabled={!connected}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-ink rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50"
                >
                  <FiRefreshCw className="w-4 h-4" />
                  Refresh Budgets
                </button>
              </div>
            </div>

            {/* Error banners */}
            {errCampaigns && (
              <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 flex items-start gap-3">
                <FiAlertTriangle className="mt-0.5 w-5 h-5 text-yellow-700" />
                <div className="text-sm text-yellow-800">
                  <div className="font-medium">Campaigns error</div>
                  <div className="mt-0.5">{errCampaigns}</div>
                </div>
              </div>
            )}
            {errBudgets && (
              <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 flex items-start gap-3">
                <FiAlertTriangle className="mt-0.5 w-5 h-5 text-yellow-700" />
                <div className="text-sm text-yellow-800">
                  <div className="font-medium">Budgets error</div>
                  <div className="mt-0.5">{errBudgets}</div>
                </div>
              </div>
            )}
          </div>

          {/* All Campaigns */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                All Campaigns <span className="text-gray-500">({filteredCampaigns.length})</span>
              </h2>
            </div>

            {loadingCampaigns ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonCard key={`c-skel-${i}`} />
                ))}
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center">
                <FiBarChart2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900">No campaigns found</h3>
                <p className="text-gray-600 mt-1">
                  {connected === false
                    ? "Connect a Google Ads account above to load campaigns."
                    : "Try adjusting the filters or search query."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredCampaigns.map((c) => (
                  <CampaignCard key={c.id} campaign={c} onNameClick={handleNameClick} />
                ))}
              </div>
            )}
          </div>

          {/* All Budgets */}
          <div className="mb-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                All Budgets <span className="text-gray-500">({filteredBudgets.length})</span>
              </h2>
              <div className="w-full md:w-80 relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  value={budgetQuery}
                  onChange={(e) => setBudgetQuery(e.target.value)}
                  placeholder="Search budgets by name, ID, amount, status…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            {loadingBudgets ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonCard key={`b-skel-${i}`} />
                ))}
              </div>
            ) : filteredBudgets.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center">
                <FiBarChart2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900">No budgets found</h3>
                <p className="text-gray-600 mt-1">
                  {connected === false
                    ? "Connect a Google Ads account above to load budgets."
                    : "Try adjusting the search."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredBudgets.map((b) => (
                  <BudgetCard key={b.id} budget={b} currency={currency} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
